import React, { useMemo, useCallback } from "react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faShoppingCart,
  faHeart as faSolidHeart,
} from "@fortawesome/free-solid-svg-icons";
import { faHeart as faRegularHeart } from "@fortawesome/free-regular-svg-icons";
import { formatMoney, url } from "@/utils";
import { Link } from "react-router-dom";
import { CourseDto } from "@/data/dtos/course.dto";
import { addToCart } from "@/actions/cart.actions";
import { addToWhistList, removeWhistList } from "@/actions/whisList.action";

const Card: React.FC<{ course: CourseDto }> = ({ course }) => {
  const dispatch = useDispatch();
  const { cart, whisList, user } = useSelector(
    (state: any) => ({
      cart: state.cart,
      whisList: state.whisList,
      user: state.auth.user,
    }),
    shallowEqual
  );

  const isInCart = useMemo(
    () => cart?.items?.some((item) => item?._id === course?._id),
    [cart, course]
  );
  const isInWishlist = useMemo(
    () => whisList?.items?.some((item) => item?._id === course?._id),
    [whisList, course]
  );
  const isOwnedByUser = useMemo(
    () =>
      user?.userCourseProgress?.some((crs) => crs?.course?._id === course?._id),
    [user, course]
  );

  const handleAddToCart = useCallback(
    () => dispatch(addToCart(course) as any),
    [dispatch, course]
  );
  const handleToggleWishlist = useCallback(() => {
    dispatch(
      isInWishlist ? removeWhistList(course) : (addToWhistList(course) as any)
    );
  }, [dispatch, course, isInWishlist]);

  return (
    <div className="bg-white shadow-lg rounded-2xl group transition-all hover:shadow-xl hover:scale-[1.02] w-full max-w-[20rem] p-4 flex flex-col h-full">
      {/* Image Section */}
      <Link
        to={url("Courses.Course-By-Slug", { slug: course.slug! })}
        className="block relative"
      >
        <div
          className="h-44 w-full bg-gray-200 rounded-xl overflow-hidden bg-cover bg-center transition-all duration-300 shadow-md"
          style={{ backgroundImage: `url("${course.images?.[0]?.url}")` }}
        />
        {/* Favorite Button */}
        <button
          onClick={handleToggleWishlist}
          className="absolute top-3 right-3 bg-white p-2 rounded-full shadow-md transition-all hover:scale-110"
        >
          <FontAwesomeIcon
            icon={isInWishlist ? faSolidHeart : faRegularHeart}
            className="text-red-500"
          />
        </button>
      </Link>

      {/* Content Section */}
      <div className="flex flex-col justify-between h-full mt-4">
        <div>
          {/* Title (Fixed Height) */}
          <h2
            className=" font-bold text-gray-900 overflow-hidden break-words min-h-[4.5rem] max-h-[4.5rem]"
            style={{
              display: "-webkit-box",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: 2.5, // Ensures max 2 lines
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "normal",
              wordBreak: "break-word",
            }}
            title={course.title}
          >
            {course.title}
          </h2>

          {/* Instructor Names */}
          <div className="text-xs text-gray-500 flex flex-wrap gap-1 mb-2 max-h-10 overflow-hidden">
            {course.instructors ? (
              course.instructors.map((instructor, index) => (
                <span
                  key={index}
                  className="bg-gray-100 px-2 py-1 rounded-full font-medium text-xs"
                >
                  {instructor?.name?.trim() || "Bilinmiyor"}{" "}
                  {instructor?.surname || ""}
                </span>
              ))
            ) : (
              <span>Yükleniyor...</span>
            )}
          </div>
        </div>

        {/* Price Section */}
        <div className="flex justify-between items-center w-full mt-3">
          <p className="text-lg font-semibold text-gray-900">
            {formatMoney(
              course.price - (course.price * course.discountPercentage) / 100
            )}
          </p>
          {course.discountPercentage > 0 && (
            <p className="text-sm text-gray-400 line-through">
              {formatMoney(course.price)}
            </p>
          )}
        </div>

        {/* Buttons Section */}
        <div className="mt-4 flex flex-col gap-2">
          {user?.role === "student" || !user ? (
            isOwnedByUser ? (
              <p className="text-violet-600 text-center text-sm font-medium w-full py-2 border border-violet-500 rounded-lg bg-violet-50">
                Bu Atölyeye Sahipsin
              </p>
            ) : (
              <button
                onClick={handleAddToCart}
                className={`w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all text-white ${
                  isInCart
                    ? "bg-gray-500 cursor-not-allowed"
                    : "bg-blue-500 hover:bg-blue-600"
                }`}
                disabled={isInCart}
              >
                {isInCart ? "Sepette" : "Sepete Ekle"}
                <FontAwesomeIcon icon={faShoppingCart} />
              </button>
            )
          ) : (
            <div className="text-gray-500 text-sm font-medium text-center w-full p-3 border border-gray-300 rounded-lg bg-gray-50">
              Eğitmen hesapları satın alım yapamaz.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Card;
